import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { Company } from '../../dto/company';
import { CustomerPreferences } from '../../dto/preferences/customer-preferences';
import { Country } from '../../dto/system-config/country';
import { isNullOrUndefined } from '../../utils/is-null-or-undefined';

enum FilerType {
  'SUN_IP',
  'CLIENT_EMAIL',
  'CLIENT_PLATFORM',
}

export enum AgentType {
  'VENDOR',
  'EMAIL'
}

@Component({
  selector: 'townip-foreign-agent-classification-tag',
  templateUrl: './foreign-agent-classification-tag.component.html',
  styleUrls: ['./foreign-agent-classification-tag.component.scss']
})
export class ForeignAgentClassificationTagComponent implements OnInit, OnChanges {

  @Input()
  public country: Country;

  // Type of foreign agent.
  // A Vendor can serve as a foreign agent, but so can a non system user,
  // who is usually identified by their email within a client.
  @Input()
  public foreignAgentType: AgentType;

  @Input()
  public foreignAgentId: number;

  @Input()
  public preferenceSet: CustomerPreferences;

  @Input()
  public forPrefSetDisplay = false;

  @Input()
  public showAsBadge = true;

  @Input()
  public company: Company;

  public badgeText: string = '';

  constructor() {
  }

  public ngOnInit(): void {}

  public ngOnChanges(): void {
    this.badgeText = ForeignAgentClassificationTagComponent
      .setupBadgeText(
        this.foreignAgentType,
        this.forPrefSetDisplay,
        this.country,
        this.preferenceSet,
        this.foreignAgentId,
        this.company
      );
  }

  public static setupBadgeText(
    foreignAgentType: AgentType,
    forPrefSetDisplay: boolean,
    country: Country,
    preferenceSet: CustomerPreferences,
    foreignAgentId: number,
    company: Company,
  ): string {
    let filerType: FilerType;

    switch (foreignAgentType) {
    case AgentType.EMAIL:
      filerType = FilerType.CLIENT_EMAIL;
      break;
    case AgentType.VENDOR:
      filerType = this.getVendorFilingType(forPrefSetDisplay, country, preferenceSet, foreignAgentId, company);
      break;
    }

    return this.badgeText(filerType);
  }

  private static getVendorFilingType(
    forPrefSetDisplay: boolean,
    country: Country,
    preferenceSet: CustomerPreferences,
    foreignAgentId: number,
    company: Company,
  ): FilerType {
    let filerType = forPrefSetDisplay ? FilerType.CLIENT_PLATFORM : FilerType.SUN_IP;

    if (isNullOrUndefined(country)) {
      return;
    }

    if (filerType === FilerType.CLIENT_PLATFORM) {
      return;
    }

    if (!isNullOrUndefined(preferenceSet) &&
        !isNullOrUndefined(preferenceSet.patentProject.foreignAgentJurisdictions)) {
      const foundVendorFiler = preferenceSet.patentProject.foreignAgentJurisdictions.find(agent => {
        if (agent.jurisdictionId === country.id &&
          agent.vendorForeignAgent && agent.vendorForeignAgent.id === foreignAgentId) {
          return true;
        }
      });

      if (!isNullOrUndefined(foundVendorFiler)) {
        filerType = FilerType.CLIENT_PLATFORM;
        return;
      }
    }

    if (!isNullOrUndefined(company) && !isNullOrUndefined(company.preferredVendors)) {
      const foundPrefVendor = company.preferredVendors.find(pv => {
        if (isNullOrUndefined(pv.jurisdiction) || pv.jurisdiction.id !== country.id) {
          return false;
        }

        if (pv.vendorId === foreignAgentId) {
          return true;
        }

        if (pv.availableVendors &&
            pv.availableVendors
              .find(av => av.vendorForeignAgent && av.vendorForeignAgent.id === foreignAgentId)) {
          return true;
        }
      });

      if (!isNullOrUndefined(foundPrefVendor)) {
        filerType = FilerType.CLIENT_PLATFORM;
      }
    }

    return filerType;
  }

  private static badgeText(filerType: FilerType): string {
    let badgeText: string = '';

    switch (filerType) {
    case FilerType.SUN_IP:
      badgeText = 'Sun IP FA';
      break;
    case FilerType.CLIENT_PLATFORM:
      badgeText = 'Client FA via Platform';
      break;
    case FilerType.CLIENT_EMAIL:
      badgeText = 'Client FA via Email';
      break;
    }

    return badgeText;
  }
}
